// LIFTED from bagel-dashboard (Object Mappings). Auth0 token plumbing neutralized:
// bagel-admin attaches the admin JWT via the axios interceptor, and axiosInstance's
// baseURL supplies the API domain — so API_DOMAIN is '' and getAuthHeaders is a no-op.
// Endpoints kept as-is (customer /integrations/v2/*); BE migrates to /admin/* later
// (see plans/admin-endpoint-migration.md).
import axiosLib from 'axios';
import { axiosInstance as axios } from '../../../api/axios-instance';
import { AvailableIntegration, Integration } from '@bageltechnologies/bagel-common';

const API_DOMAIN = '';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getAuthHeaders = async (_auth0?: unknown) => ({});

export const getAvailableIntegrations = async (auth0: any): Promise<AvailableIntegration[]> => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_DOMAIN}/integrations/v2/available`, config);
    return response.data;
  } catch (e) {
    console.error('Error fetching available integrations:', e);
    throw e;
  }
};

export const getIntegrations = async (auth0: any): Promise<Integration[]> => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_DOMAIN}/integrations/v2/connected`, config);
    return response.data;
  } catch (e) {
    console.error('Error fetching connected integrations:', e);
    throw e;
  }
};

export const getIntegrationByKey = async (
  key: string,
  auth0: any,
): Promise<{ available: AvailableIntegration; connected?: Integration }> => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_DOMAIN}/integrations/v2/${key}`, config);
    return response.data;
  } catch (e) {
    console.error(`Error fetching integration ${key}:`, e);
    throw e;
  }
};

export const updateIntegrationConfig = async (
  key: string,
  config: Partial<Integration>,
  auth0: any,
): Promise<Integration> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.put(
      `${API_DOMAIN}/integrations/v2/${key}/config`,
      config,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error updating integration ${key} config:`, e);
    throw e;
  }
};

export const saveIntegrationSelectedScopes = async (
  key: string,
  selectedScopes: string[],
  auth0: any,
): Promise<any> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/oauth/scopes`,
      { selectedScopes },
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error saving selected scopes for integration ${key}:`, e);
    throw e;
  }
};

export interface FieldsResponse {
  fields: Record<string, any>[];
  origin: string;
  blacklistedFields?: string[];
}

export interface IntegrationTask {
  _id: string;
  type: string;
  objectName: string;
  accountId: string;
  origin: string;
  options: {
    objectName: string;
    startDate: string;
    endDate: string;
    dateField: string;
  };
  execution: {
    startDate: string;
    status: 'pending' | 'in_progress' | 'completed' | 'failed';
    finishDate?: string;
    stats?: {
      apiCallCount?: number;
      receivedCount?: number;
      processedCount?: number;
    };
  };
  lastModified: string;
}

export interface TasksResponse {
  tasks: IntegrationTask[];
  pagination: {
    skip: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export const getIntegrationFields = async (
  accountId: string,
  key: string,
  object: string,
  originObject: string,
): Promise<FieldsResponse> => {
  try {
    // Admin object2.0 field mappings — GET /admin/object-fields (behaviour-preserving twin).
    const response = await axios.get(`${API_DOMAIN}/admin/object-fields`, {
      params: { accountId, origin: key, object, originObjectKey: originObject },
    });
    return response.data;
  } catch (e) {
    // If fields are not found, return empty fields object with the integration key as origin
    if (axiosLib.isAxiosError(e) && e.response?.status === 404) {
      return {
        fields: [],
        origin: key,
      };
    }
    console.error(`Error getting integration ${key} fields for ${object}:`, e);
    throw e;
  }
};

export type UpdateIntegrationFieldsRequest =
  | {
      fieldName: string;
      field: {
        fieldType: 'imported' | 'processed' | 'static' | 'template';
        [key: string]: any;
      };
    }
  | {
      blacklistField: {
        action: 'add' | 'remove';
        field: string;
      };
    };

export type UpdateIntegrationFieldsResponse =
  | {
      success: boolean;
      modifiedCount: number;
      upsertedId?: string;
    }
  | {
      success: boolean;
      blacklistedFields: string[];
      modifiedCount: number;
      upsertedId?: string;
    };

export const updateIntegrationFields = async (
  accountId: string,
  key: string,
  object: string,
  originObject: string,
  fieldData: UpdateIntegrationFieldsRequest,
): Promise<UpdateIntegrationFieldsResponse> => {
  try {
    const payload =
      'field' in fieldData && fieldData.field
        ? {
            ...fieldData,
            field: {
              ...fieldData.field,
              syncDirection: fieldData.field.syncDirection ?? 'in',
            },
          }
        : fieldData;
    // Admin PUT /admin/object-fields — Mode A (upsert field) or Mode B (blacklist toggle).
    const response = await axios.put(`${API_DOMAIN}/admin/object-fields`, payload, {
      params: { accountId, origin: key, object, originObjectKey: originObject },
    });
    return response.data;
  } catch (e) {
    console.error(`Error updating integration ${key} fields for ${object}:`, e);
    throw e;
  }
};

export interface ValidationSample {
  input: Record<string, any>;
  output: any;
  error?: string;
}

export interface ValidationResponse {
  fieldName: string;
  samples: ValidationSample[];
  successCount: number;
  errorCount: number;
}

export const validateProcessedField = async (
  key: string,
  object: string,
  originObject: string,
  fieldName: string,
  sampleCount: number,
  auth0: any,
): Promise<ValidationResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/object2.0/${object}/originObject/${originObject}/validate-field`,
      { fieldName, sampleCount },
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error validating field ${fieldName}:`, e);
    throw e;
  }
};

export interface TemplatePreviewResponse {
  fieldName: string;
  preview: string | null;
  sampleData?: any;
  message?: string;
}

export const validateTemplateField = async (
  key: string,
  object: string,
  originObject: string,
  fieldName: string,
  template: string,
  auth0: any,
): Promise<TemplatePreviewResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/object2.0/${object}/originObject/${originObject}/validate-template`,
      { fieldName, template, sampleCount: 1 },
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error validating template field ${fieldName}:`, e);
    throw e;
  }
};

export type DeleteIntegrationFieldsRequest =
  | {
      fieldName: string;
    }
  | {
      field: {
        key: string;
        type?: string;
        origin: string;
        originName?: string;
      };
    };

export type DeleteIntegrationFieldsResponse = {
  success: boolean;
  deletedCount: number;
  modifiedCount: number;
  deletedFields: string[];
};

export const deleteIntegrationFields = async (
  accountId: string,
  key: string,
  object: string,
  originObject: string,
  fieldData: DeleteIntegrationFieldsRequest,
): Promise<DeleteIntegrationFieldsResponse> => {
  try {
    // Admin DELETE /admin/object-fields — body carries { fieldName } or { field }.
    const response = await axios.delete(`${API_DOMAIN}/admin/object-fields`, {
      params: { accountId, origin: key, object, originObjectKey: originObject },
      data: fieldData,
    });
    return response.data;
  } catch (e) {
    console.error(`Error deleting integration ${key} fields for ${object}:`, e);
    throw e;
  }
};

export const updateIntegrationApiKey = async (
  key: string,
  auth0: any,
  tokenObject?: any,
): Promise<Integration> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.put(
      `${API_DOMAIN}/integrations/v2/${key}/apiKey`,
      tokenObject,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error updating integration ${key} config:`, e);
    throw e;
  }
};

export const connectIntegration = async (
  key: string,
  code: string,
  auth0: any,
): Promise<Integration> => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/connect`,
      { code },
      config,
    );
    return response.data;
  } catch (e) {
    console.error(`Error connecting integration ${key}:`, e);
    throw e;
  }
};

export const disconnectIntegration = async (key: string, auth0: any): Promise<void> => {
  try {
    const config = await getAuthHeaders(auth0);
    await axios.post(`${API_DOMAIN}/integrations/v2/${key}/disconnect`, {}, config);
  } catch (e) {
    console.error(`Error disconnecting integration ${key}:`, e);
    throw e;
  }
};

export const getOAuthUrl = async (key: string, auth0: any): Promise<{ url: string }> => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_DOMAIN}/integrations/v2/${key}/oauth-url`, config);
    return response.data;
  } catch (e) {
    console.error(`Error getting OAuth URL for ${key}:`, e);
    throw e;
  }
};

export const getConnectionOptions = async (key: string, auth0: any): Promise<{ url: string }> => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(
      `${API_DOMAIN}/integrations/v2/${key}/connection-options`,
      config,
    );
    return response.data;
  } catch (e) {
    console.error(`Error getting OAuth URL for ${key}:`, e);
    throw e;
  }
};

/**
 * Trigger a sync run for one or more objects of an integration.
 * @param auth0 Auth object for authentication
 * @param key Integration key (e.g., 'salesforce', 'zendesk')
 * @param objects Array of object names to sync (e.g., ['accounts', 'opportunities'])
 * @param dateRange Optional date range configuration
 * @returns The triggered sync run results
 */
export const triggerIntegrationSyncRun = async (
  auth0: any,
  key: string,
  objects: string[],
  dateRange?: {
    start: string;
    end: string;
    dateField?: string;
  },
  isSampleRun = false,
): Promise<any> => {
  try {
    const config = await getAuthHeaders(auth0);
    const url = `/integrations/v2/${key}/runs`;
    const requestBody: any = { objects };

    if (dateRange) {
      requestBody.dateRange = dateRange;
    }

    if (isSampleRun) {
      requestBody.isSampleRun = true;
    }

    const response = await axios.post(url, requestBody, config);
    return response.data;
  } catch (e) {
    console.error('Error triggering integration sync run', e);
    throw e;
  }
};

/**
 * Fetch a paginated list of tasks for an integration.
 * @param auth0 Auth object for authentication
 * @param key Integration key (e.g., 'salesforce', 'zendesk')
 * @param skip How many tasks to skip (for pagination, default 0)
 * @param limit How many tasks to return (default 20)
 * @param status Optional status parameter for filtering tasks (can be comma-separated string)
 * @returns TasksResponse with tasks array and pagination info
 */
export const getIntegrationTasks = async (
  auth0: any,
  key: string,
  skip = 0,
  limit = 20,
  status?: string,
): Promise<TasksResponse> => {
  try {
    const config = await getAuthHeaders(auth0);
    let url = `/integrations/v2/${key}/tasks?skip=${skip}&limit=${limit}`;
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }

    const response = await axios.get(url, config);
    return response.data;
  } catch (e) {
    console.error('Error fetching integration tasks', e);
    throw e;
  }
};

/**
 * Fetch paginated tasks for a specific integration object.
 * @param auth0 Auth object for authentication
 * @param key Integration key (e.g., 'salesforce')
 * @param object Object name (e.g., 'accounts')
 * @param skip How many tasks to skip (for pagination, default 0)
 * @param limit How many tasks to return (default 20)
 * @param status Optional status parameter for filtering tasks (can be comma-separated string)
 * @returns TasksResponse with tasks array and pagination info for the specified object
 */
export const fetchIntegrationObjectTasks = async (
  auth0: any,
  key: string,
  object: string,
  skip = 0,
  limit = 20,
  status?: string,
): Promise<TasksResponse> => {
  try {
    const config = await getAuthHeaders(auth0);
    let url = `${API_DOMAIN}/integrations/v2/${key}/tasks/${object}?skip=${skip}&limit=${limit}`;
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }
    console.log('fetchIntegrationObjectTasks', url);

    const response = await axios.get(url, config);
    return response.data;
  } catch (e) {
    console.error(`Error fetching tasks for integration ${key}, object ${object}:`, e);
    throw e;
  }
};

/**
 * Triggers a metadata sync for a specific object of an integration and returns the updated metadata and sync result.
 * @param key Integration key (e.g., 'salesforce', 'jira')
 * @param object Object name to update metadata for (e.g., 'Opportunity', 'Ticket')
 * @param auth0 Auth object for authentication
 * @returns The updated object metadata and sync result
 */
export const updateIntegrationObjectMeta = async (
  key: string,
  object: string,
  auth0: any,
): Promise<any> => {
  try {
    const config = await getAuthHeaders(auth0);
    const url = `${API_DOMAIN}/integrations/v2/${key}/runs/${object}/update-meta`;
    const response = await axios.put(url, undefined, config);
    return response.data;
  } catch (e) {
    console.error(`Error updating metadata for integration ${key}, object ${object}:`, e);
    throw e;
  }
};

/**
 * Get the count of processed and unprocessed items for a given integration and object.
 * @param auth0 Auth object for authentication
 * @param key Integration key (e.g., 'airtable')
 * @param object Object name (e.g., 'survey')
 * @returns {Promise<{ processed: number, unprocessed: number }>} The counts of processed and unprocessed items
 */
export const getUnprocessedItemCount = async (
  auth0: any,
  key: string,
  originObjectKey: string,
): Promise<{ processed: number; unprocessed: number }> => {
  try {
    const config = await getAuthHeaders(auth0);
    const url = `${API_DOMAIN}/integrations/v2/${key}/process/${originObjectKey}/count`;
    const response = await axios.get(url, config);
    return response.data;
  } catch (e) {
    console.error(
      `Error fetching processed and unprocessed item counts for integration ${key}, object ${originObjectKey}:`,
      e,
    );
    throw e;
  }
};

/**
 * Process all unprocessed items for a given integration and object.
 * @param auth0 Auth object for authentication
 * @param key Integration key (e.g., 'airtable')
 * @param originObjectKey Origin object key (e.g., 'survey')
 * @returns {Promise<{ processed: number, failed: number, errors: any[] }>} Processing result
 */
export const processUnprocessedItems = async (
  auth0: any,
  key: string,
  originObjectKey: string,
): Promise<{ processed: number; failed: number; errors: any[] }> => {
  try {
    const config = await getAuthHeaders(auth0);
    const url = `${API_DOMAIN}/integrations/v2/${key}/process/${originObjectKey}`;
    const response = await axios.post(url, undefined, config);
    return response.data;
  } catch (e) {
    console.error(
      `Error processing unprocessed items for integration ${key}, originObjectKey ${originObjectKey}:`,
      e,
    );
    throw e;
  }
};

/**
 * Process all unprocessed items for a given integration and object.
 * @param auth0 Auth object for authentication
 * @param key Integration key (e.g., 'airtable')
 * @param originObjectKey Origin object key (e.g., 'survey')
 * @returns {Promise<{ processed: number, failed: number, errors: any[] }>} Processing result
 */
export const processUnprocessedSampleItems = async (
  auth0: any,
  key: string,
  originObjectKey: string,
): Promise<{ processed: number; failed: number; errors: any[] }> => {
  try {
    const config = await getAuthHeaders(auth0);
    const url = `${API_DOMAIN}/integrations/v2/${key}/process/samples/${originObjectKey}`;
    const response = await axios.post(url, undefined, config);
    return response.data;
  } catch (e) {
    console.error(
      `Error processing unprocessed items for integration ${key}, originObjectKey ${originObjectKey}:`,
      e,
    );
    throw e;
  }
};

/**
 * Get the count of missing organizations for a given integration and object.
 * @param auth0 Auth object for authentication
 * @param key Integration key (e.g., 'salesforce')
 * @param object Object2.0 name (e.g., 'opportunity')
 * @param originObjectKey Origin object key from external system (e.g., 'Opportunity')
 * @returns {Promise<{ itemType: string, integrationKey: string, mappedField: string, missingOrgsCount: number }>} Missing organizations count info
 */
export const getMissingOrganizationsCount = async (
  auth0: any,
  key: string,
  object: string,
  originObjectKey: string,
): Promise<{
  itemType: string;
  integrationKey: string;
  mappedField: string;
  missingOrgsCount: number;
}> => {
  try {
    const config = await getAuthHeaders(auth0);
    const url = `${API_DOMAIN}/integrations/v2/${key}/missing-orgs/${object}/originObject/${originObjectKey}/count`;
    const response = await axios.get(url, config);
    return response.data;
  } catch (e) {
    console.error(
      `Error fetching missing organizations count for integration ${key}, object ${object}, originObject ${originObjectKey}:`,
      e,
    );
    throw e;
  }
};

/**
 * Fill missing organizations for a given integration and object.
 * @param auth0 Auth object for authentication
 * @param key Integration key (e.g., 'salesforce')
 * @param object Object2.0 name (e.g., 'opportunity')
 * @param originObjectKey Origin object key from external system (e.g., 'Opportunity')
 * @returns {Promise<{ itemType: string, integrationKey: string, mappedField: string, totalMissingOrgs: number, batchesProcessed: number, totalInserted: number }>} Fill organizations result
 */
export const fillMissingOrganizations = async (
  auth0: any,
  key: string,
  object: string,
  originObjectKey: string,
): Promise<{
  itemType: string;
  integrationKey: string;
  mappedField: string;
  totalMissingOrgs: number;
  batchesProcessed: number;
  totalInserted: number;
}> => {
  try {
    const config = await getAuthHeaders(auth0);
    const url = `${API_DOMAIN}/integrations/v2/${key}/missing-orgs/${object}/originObject/${originObjectKey}/fill`;
    const response = await axios.post(url, undefined, config);
    return response.data;
  } catch (e) {
    console.error(
      `Error filling missing organizations for integration ${key}, object ${object}, originObject ${originObjectKey}:`,
      e,
    );
    throw e;
  }
};

/**
 * One profiled field's stats from `GET /admin/field-statistics` (stats only; metadata lives in
 * `fieldsMeta`). Aligned with the dashboard's `FieldStatisticItem` (PR #2104).
 */
export interface FieldStatisticItem {
  /** Join key — merge onto `fieldsMeta[].key`. */
  fieldKey: string;
  fieldName: string;
  statisticsId?: string;
  /** 0–100. */
  fillPercentage: number;
  totalRecords: number;
  nonNullCount: number;
  /** BSON types, not the describe type. */
  types?: string[];
  uniqueValueCount?: number;
  /** If true, render uniqueValueCount as "{count}+". */
  hasMoreUniqueValues?: boolean;
  commonValues?: Array<{ value: string; count: number; percentage: number }>;
  values?: string[];
  /** Numeric/date fields only. */
  min?: number | string;
  max?: number | string;
  lastCalculated?: string;
}

export interface FieldStatisticsResponse {
  items: FieldStatisticItem[];
  total: number;
  hasMore: boolean;
}

/**
 * Field statistics (stats only) — admin twin of the dashboard's `getFieldStatistics` (PR #2104).
 * `GET /admin/field-statistics?accountId&integrationKey&originObjectKey`; returns all profiled
 * fields in one call. Merge `items` onto `fieldsMeta` by `fieldKey`. Replaces the removed
 * `explore-fields`. See ai-context/handoffs/admin-field-statistics-frontend.md.
 */
export const getFieldStatistics = async (
  accountId: string,
  key: string,
  originObjectKey: string,
): Promise<FieldStatisticsResponse> => {
  try {
    const response = await axios.get<FieldStatisticsResponse>(
      `${API_DOMAIN}/admin/field-statistics`,
      { params: { accountId, integrationKey: key, originObjectKey } },
    );
    return response.data;
  } catch (e) {
    console.error(
      `Error fetching field statistics for integration ${key}, originObject ${originObjectKey}:`,
      e,
    );
    throw e;
  }
};

export interface PrepareMetaResponse {
  availableObjects: Array<{
    name: string;
    bagelObjectKey: string;
    fields: Array<{
      name: string;
      type: string;
      fieldPath: string;
    }>;
  }>;
  result: {
    tasksCompleted: number;
    itemsProcessed: number;
    errors: any[];
    metadataSynced: boolean;
  };
}

/**
 * Syncs and prepares integration metadata by fetching available objects from the integration platform.
 * @param key Integration key (e.g., 'notion', 'salesforce')
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<PrepareMetaResponse> - Available objects and sync result
 */
export const prepareIntegrationMeta = async (
  key: string,
  auth0: any,
): Promise<PrepareMetaResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/prepare-meta`,
      {},
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error preparing integration ${key} metadata:`, e);
    throw e;
  }
};

export interface TestCredentialsResponse {
  valid: boolean;
  message: string;
  integrationKey: string;
  tested: {
    date: string;
    result: boolean;
    error?: string;
  };
  error?: string;
}

/**
 * Tests if integration credentials are valid and saves the test result to the integration.
 * @param key Integration key (e.g., 'salesforce', 'notion')
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<TestCredentialsResponse> - Validation result and test metadata
 */
export const testIntegrationCredentials = async (
  key: string,
  auth0: any,
): Promise<TestCredentialsResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/test-credentials`,
      {},
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error testing integration ${key} credentials:`, e);
    throw e;
  }
};

export interface MLProcessedCountResponse {
  integrationKey: string;
  originObjectKey: string;
  totalItemsCount: number;
  totalMLEligible: number;
  mlProcessedCount: number;
  mlPendingCount: number;
  mlFilters: any;
  timestamp: string;
}

/**
 * Get ML processed count for a specific integration object.
 * @param key Integration key (e.g., 'salesforce', 'zendesk')
 * @param originObjectKey Object name (e.g., 'Case', 'Ticket')
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<MLProcessedCountResponse> - ML processing count information
 */
export const getMLProcessedCount = async (
  key: string,
  originObjectKey: string,
  auth0: any,
): Promise<MLProcessedCountResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.get(
      `${API_DOMAIN}/integrations/v2/${key}/ml/${originObjectKey}/processed-count`,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(
      `Error getting ML processed count for integration ${key}, object ${originObjectKey}:`,
      e,
    );
    throw e;
  }
};

export interface MLItemUpdateRequest {
  updateType: string;
  processAll: boolean;
  additionalData?: any;
}

export interface MLItemUpdateResponse {
  success: boolean;
  integrationKey: string;
  originObjectKey: string;
  processedCount: number;
  processAll: boolean;
  processedItems: Array<{
    cxItemId: string;
    originId: string;
    itemType: string;
    wasAlreadyProcessed: boolean;
  }>;
  updateType: string;
  timestamp: string;
  message: string;
}

/**
 * Send ML item update for a specific integration object.
 * @param key Integration key (e.g., 'salesforce', 'zendesk')
 * @param originObjectKey Object name (e.g., 'Case', 'Ticket')
 * @param updateData ML update data
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<MLItemUpdateResponse> - ML update result
 */
export const sendMLItemUpdate = async (
  key: string,
  originObjectKey: string,
  updateData: MLItemUpdateRequest,
  auth0: any,
): Promise<MLItemUpdateResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/ml/${originObjectKey}/item-update`,
      updateData,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(
      `Error sending ML item update for integration ${key}, object ${originObjectKey}:`,
      e,
    );
    throw e;
  }
};

export interface UpdateExternalObjectRequest {
  [key: string]: any;
}

export interface UpdateExternalObjectResponse {
  success: boolean;
  modifiedCount: number;
  matchedCount: number;
}

/**
 * Update an external object configuration for a specific integration.
 * @param key Integration key (e.g., 'airtable', 'salesforce')
 * @param originObjectKey Origin object key (e.g., 'ideas', 'opportunities')
 * @param updates Object containing fields to update
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<UpdateExternalObjectResponse> - Update result with counts
 */
export interface AddExternalObjectRequest {
  originObjectKey: string;
  originObject?: string;
  name?: string;
  bagelObjectKey?: string;
  isDisabled?: boolean;
  [field: string]: unknown;
}

/**
 * Add a single external object to an integration.
 * POST /integrations/v2/:key/external-object
 */
export const addExternalObject = async (
  key: string,
  externalObject: AddExternalObjectRequest,
  auth0: any,
): Promise<{ success: boolean; externalObject: ExternalObjectConf }> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/external-object`,
      externalObject,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error adding external object to integration ${key}:`, e);
    throw e;
  }
};

/**
 * Remove a single external object from an integration.
 * Ingested items (original_items / cx_items) are intentionally left in place.
 * DELETE /integrations/v2/:key/external-object/:originObjectKey
 */
export const deleteExternalObject = async (
  key: string,
  originObjectKey: string,
  auth0: any,
): Promise<{ success: boolean; originObjectKey: string; integrationObjectsDeleted: number }> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.delete(
      `${API_DOMAIN}/integrations/v2/${key}/external-object/${encodeURIComponent(originObjectKey)}`,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error deleting external object ${originObjectKey} from integration ${key}:`, e);
    throw e;
  }
};

export const updateExternalObject = async (
  key: string,
  originObjectKey: string,
  updates: UpdateExternalObjectRequest,
  auth0: any,
): Promise<UpdateExternalObjectResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.patch(
      `${API_DOMAIN}/integrations/v2/${key}/external-object/${originObjectKey}`,
      updates,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(
      `Error updating external object for integration ${key}, object ${originObjectKey}:`,
      e,
    );
    throw e;
  }
};

export interface SetupOriginObjectResponse {
  success: boolean;
  taskId: string;
  sampleSessionId: string;
}

export interface OriginObjectSetupStatus {
  status: 'not_started' | 'pending' | 'syncing' | 'calculating_stats' | 'ready' | 'error';
  tasks: { total: number; completed: number; failed: number };
  hasStatistics: boolean;
  error?: string;
}

/**
 * POST /integrations/v2/:key/setup-origin-object/:originObjectKey
 */
export const setupOriginObject = async (
  key: string,
  originObjectKey: string,
  options: Record<string, unknown> | undefined,
  auth0: any,
): Promise<SetupOriginObjectResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const encoded = encodeURIComponent(originObjectKey);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/setup-origin-object/${encoded}`,
      options ?? {},
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(
      `Error starting origin object setup for integration ${key}, object ${originObjectKey}:`,
      e,
    );
    throw e;
  }
};

/**
 * GET /integrations/v2/:key/origin-object-setup-status/:originObjectKey
 */
export const getOriginObjectSetupStatus = async (
  key: string,
  originObjectKey: string,
  auth0: any,
): Promise<OriginObjectSetupStatus> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const encoded = encodeURIComponent(originObjectKey);
    const response = await axios.get(
      `${API_DOMAIN}/integrations/v2/${key}/origin-object-setup-status/${encoded}`,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(
      `Error fetching origin object setup status for integration ${key}, object ${originObjectKey}:`,
      e,
    );
    throw e;
  }
};

export interface UploadRecordsRequest {
  feedbackName: string;
  feedbackCategory: string;
  originSystem: string;
  records: Array<{
    originId: string;
    linkedAccountId: string;
    createdAt: string;
    submitterEmail?: string;
    title?: string;
    description?: string;
    priority?: string;
    [key: string]: any;
  }>;
}

export interface UploadRecordsResponse {
  success: boolean;
  integration: string;
  summary: {
    processedCount: number;
    createdCount: number;
    updatedCount: number;
    errors: any[];
  };
}

/**
 * Upload records directly to a specific integration object using the same processing logic as the sync API.
 * @param key Integration key (e.g., 'gong', 'salesforce', 'jira')
 * @param recordsData Request data containing feedback configuration and records to upload
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<UploadRecordsResponse> - Upload result with processing summary
 */
export const uploadIntegrationRecords = async (
  key: string,
  recordsData: UploadRecordsRequest,
  auth0: any,
): Promise<UploadRecordsResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/upload-records`,
      recordsData,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error uploading records for integration ${key}:`, e);
    throw e;
  }
};

/**
 * Trigger a count exploration for a specific integration object.
 * GET /integrations/v2/:integrationId/objects/:objectKey/count
 * @param auth0 Auth object for authentication
 * @param key Integration key
 * @param originObjectKey External object key
 * @param dateRange Optional date range
 * @param filters Optional filters rule group
 */
export const triggerSyncCount = async (
  auth0: any,
  key: string,
  originObjectKey: string,
  dateRange?: { start?: string; end?: string },
  filters?: any,
  dateField?: string,
): Promise<{ count: number }> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const params: any = {};
    if (dateRange?.start) params.startDate = dateRange.start;
    if (dateRange?.end) params.endDate = dateRange.end;
    if (dateField) params.dateField = dateField;
    if (filters) params.filters = filters;

    const url = `${API_DOMAIN}/integrations/v2/${key}/objects/${originObjectKey}/count`;
    const response = await axios.post(url, params, authConfig);
    return response.data;
  } catch (e) {
    console.error(`Error triggering count for integration ${key}, object ${originObjectKey}:`, e);
    throw e;
  }
};

// Data Out API Functions

export interface BagelFieldsResponse {
  actionKey: string;
  actionContext: string;
  fields: Array<{
    name: string;
    key: string;
    type: string;
    path: string;
    isCalculated?: boolean;
    isRelation?: boolean;
    description?: string;
  }>;
}

/**
 * Get available bagel actions for an integration
 * @param integrationKey Integration key (e.g., 'productboard')
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<any[]> - Available bagel actions for the integration
 */
export const getBagelActions = async (integrationKey: string, auth0: any): Promise<any[]> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.get(
      `${API_DOMAIN}/integrations/v2/${integrationKey}/data-out/actions`,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error fetching bagel actions for integration ${integrationKey}:`, e);
    throw e;
  }
};

/**
 * Get available Bagel fields for a specific action
 * @param actionKey Action key (e.g., 'create_product_item')
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<BagelFieldsResponse> - Available Bagel fields for the action
 */
export const getBagelFields = async (
  actionKey: string,
  auth0: any,
): Promise<BagelFieldsResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.get(
      `${API_DOMAIN}/integrations/v2/bagel-fields/${actionKey}`,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error fetching Bagel fields for action ${actionKey}:`, e);
    throw e;
  }
};

/**
 * Get data out configs for an integration
 * @param key Integration key (e.g., 'airtable')
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<any[]> - Data out configurations
 */
export const getDataOutConfigs = async (key: string, auth0: any): Promise<any[]> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.get(
      `${API_DOMAIN}/integrations/v2/${key}/data-out/configs`,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error fetching data out configs for integration ${key}:`, e);
    throw e;
  }
};

/**
 * Get all data out configs for a specific action across all integrations
 * @param actionKey Action key (e.g., 'create_item_from_idea')
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<any[]> - Data out configurations with integration info
 */
export const getDataOutConfigsByAction = async (actionKey: string, auth0: any): Promise<any[]> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.get(
      `${API_DOMAIN}/integrations/v2/data-out/configs/action/${actionKey}`,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error fetching data out configs for action ${actionKey}:`, e);
    throw e;
  }
};

/**
 * Create a new data out config
 * @param key Integration key (e.g., 'airtable')
 * @param config Data out configuration
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<any> - Created config response
 */
export const createDataOutConfig = async (key: string, config: any, auth0: any): Promise<any> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/data-out/configs`,
      config,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error creating data out config for integration ${key}:`, e);
    throw e;
  }
};

/**
 * Update an existing data out config
 * @param key Integration key (e.g., 'airtable')
 * @param configId Config ID to update
 * @param config Updated configuration
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<any> - Update response
 */
export const updateDataOutConfig = async (
  key: string,
  configId: string,
  config: any,
  auth0: any,
): Promise<any> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.put(
      `${API_DOMAIN}/integrations/v2/${key}/data-out/configs/${configId}`,
      config,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error updating data out config ${configId} for integration ${key}:`, e);
    throw e;
  }
};

/**
 * Delete a data out config
 * @param key Integration key (e.g., 'airtable')
 * @param configId Config ID to delete
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<any> - Delete response
 */
export const deleteDataOutConfig = async (
  key: string,
  configId: string,
  auth0: any,
): Promise<any> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.delete(
      `${API_DOMAIN}/integrations/v2/${key}/data-out/configs/${configId}`,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error deleting data out config ${configId} for integration ${key}:`, e);
    throw e;
  }
};

/**
 * Execute data out for an integration
 * @param key Integration key (e.g., 'airtable')
 * @param bagelAction Action key (e.g., 'create_product_item')
 * @param bagelData Data to send
 * @param auth0 Auth0 instance for authentication
 * @param originObjectKey Optional origin object key
 * @returns Promise<any> - Execution result
 */
export const executeDataOut = async (
  key: string,
  bagelAction: string,
  bagelData: any,
  auth0: any,
  originObjectKey?: string,
): Promise<any> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/data-out/execute`,
      {
        bagelAction,
        bagelData,
        originObjectKey,
      },
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error executing data out for integration ${key}:`, e);
    throw e;
  }
};

/**
 * Get data out execution logs
 * @param key Integration key (e.g., 'airtable')
 * @param auth0 Auth0 instance for authentication
 * @param skip Number of records to skip (for pagination)
 * @param limit Number of records to return
 * @param status Optional status filter
 * @returns Promise<any> - Execution logs
 */
export const getDataOutLogs = async (
  key: string,
  auth0: any,
  skip = 0,
  limit = 20,
  status?: string,
): Promise<any> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    let url = `${API_DOMAIN}/integrations/v2/${key}/data-out/logs?skip=${skip}&limit=${limit}`;
    if (status) {
      url += `&status=${encodeURIComponent(status)}`;
    }
    const response = await axios.get(url, authConfig);
    return response.data;
  } catch (e) {
    console.error(`Error fetching data out logs for integration ${key}:`, e);
    throw e;
  }
};

/**
 * Update a field mapping in data out config
 * @param key Integration key (e.g., 'airtable')
 * @param originObjectKey Origin object key
 * @param fieldName Field name to update
 * @param fieldMapping Field mapping data
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<any> - Update response
 */
export const updateDataOutConfigField = async (
  key: string,
  originObjectKey: string,
  fieldName: string,
  fieldMapping: any,
  auth0: any,
): Promise<any> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.put(
      `${API_DOMAIN}/integrations/v2/${key}/data-out/${originObjectKey}/field`,
      { fieldName, fieldMapping },
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error updating data out field ${fieldName} for integration ${key}:`, e);
    throw e;
  }
};

/**
 * Delete a field mapping from data out config
 * @param key Integration key (e.g., 'airtable')
 * @param originObjectKey Origin object key
 * @param fieldName Field name to delete
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<any> - Delete response
 */
export const deleteDataOutConfigField = async (
  key: string,
  originObjectKey: string,
  fieldName: string,
  auth0: any,
): Promise<any> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.delete(
      `${API_DOMAIN}/integrations/v2/${key}/data-out/${originObjectKey}/field`,
      { ...authConfig, data: { fieldName } },
    );
    return response.data;
  } catch (e) {
    console.error(`Error deleting data out field ${fieldName} for integration ${key}:`, e);
    throw e;
  }
};

export interface DataOutTemplatePreviewResponse {
  bagelAction: string;
  ideaId?: string;
  preview: string | null;
  placeholders: string[];
  missingFields?: string[];
  message?: string;
}

export const validateDataOutTemplate = async (
  key: string,
  template: string,
  auth0: any,
  options?: { bagelAction?: string; ideaId?: string },
): Promise<DataOutTemplatePreviewResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/data-out/validate-template`,
      {
        template,
        bagelAction: options?.bagelAction,
        ideaId: options?.ideaId,
      },
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error('Error validating data out template:', e);
    throw e;
  }
};

/**
 * Get all connected integrations
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<any[]> - List of connected integrations
 */
export const getConnectedIntegrations = async (auth0: any): Promise<any[]> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_DOMAIN}/integrations/v2/connected`, authConfig);
    return response.data || [];
  } catch (e) {
    console.error('Error fetching connected integrations:', e);
    throw e;
  }
};

export const getProductIntegrations = async (auth0: any): Promise<any[]> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.get(
      `${API_DOMAIN}/integrations/v2/product-integrations`,
      authConfig,
    );
    return response.data || [];
  } catch (e) {
    console.error('Error fetching product integrations:', e);
    throw e;
  }
};

export interface FullInitialSyncResponse {
  success: boolean;
  integrationKey: string;
  originObjectKey: string;
}

/**
 * Trigger a full initial sync for a specific integration object.
 * This will sync all items without date range restrictions.
 * @param key Integration key (e.g., 'salesforce', 'zendesk')
 * @param originObjectKey Object name (e.g., 'Case', 'Ticket')
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<FullInitialSyncResponse> - Sync result
 */
export const fullInitialSync = async (
  key: string,
  originObjectKey: string,
  auth0: any,
): Promise<FullInitialSyncResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    // Trigger sync run without date range for full initial sync
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/full-initial-sync/${originObjectKey}`,
      {},
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(
      `Error triggering full initial sync for integration ${key}, object ${originObjectKey}:`,
      e,
    );
    throw e;
  }
};

export interface RemapResponse {
  success: boolean;
  integrationKey: string;
  originObjectKey: string;
}

/**
 * Remap (reprocess) all original items for a specific integration object.
 * This marks all items as pending processing and triggers worker processing.
 * @param key Integration key (e.g., 'salesforce', 'zendesk')
 * @param originObjectKey Object name (e.g., 'Case', 'Ticket')
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<RemapResponse> - Remap result
 */
export const remap = async (
  key: string,
  originObjectKey: string,
  auth0: any,
): Promise<RemapResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/re-map/${originObjectKey}`,
      {},
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error remapping items for integration ${key}, object ${originObjectKey}:`, e);
    throw e;
  }
};

export interface CalculateFieldStatsResponse {
  success: boolean;
  integrationKey: string;
  originObjectKey: string;
}

/**
 * Calculate field statistics for a specific integration object.
 * This triggers a worker message to calculate and save field statistics.
 * @param key Integration key (e.g., 'salesforce', 'zendesk')
 * @param originObjectKey Object name (e.g., 'Case', 'Ticket')
 * @param auth0 Auth0 instance for authentication
 * @returns Promise<CalculateFieldStatsResponse> - Calculation result
 */
export const calculateFieldStats = async (
  key: string,
  originObjectKey: string,
  auth0: any,
): Promise<CalculateFieldStatsResponse> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    // Trigger field stats calculation by sending a worker message
    // This endpoint will send a CalculateExploreFields message to workers
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/calculate-field-stats/${originObjectKey}`,
      {},
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(
      `Error calculating field stats for integration ${key}, object ${originObjectKey}:`,
      e,
    );
    throw e;
  }
};

/** Registered inbound webhooks (Integration 2.0) — one document per origin object + event */
export interface IntegrationWebhookRecord {
  _id: string;
  accountId: string;
  integrationKey: string;
  originObjectKey: string;
  event: string;
  externalWebhookId: string;
  endpointUrl: string;
  enabled: boolean;
  status: 'active' | 'error';
  createdAt: string;
  updatedAt: string;
}

export interface WebhookCallLog {
  _id: string;
  originObjectKey?: string;
  event: string;
  rawBody: unknown;
  status: 'processed' | 'skipped' | 'error';
  skipReason?: string;
  errorMessage?: string;
  receivedAt: string;
}

export interface WebhookLogsPage {
  docs: WebhookCallLog[];
  total: number;
  page: number;
  limit: number;
}

export const getIntegrationWebhooks = async (
  auth0: any,
  key: string,
): Promise<IntegrationWebhookRecord[]> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_DOMAIN}/integrations/v2/${key}/webhooks`, authConfig);
    return response.data;
  } catch (e) {
    console.error(`Error fetching webhooks for integration ${key}:`, e);
    throw e;
  }
};

export const createIntegrationWebhook = async (
  auth0: any,
  key: string,
  body: { originObjectKey: string; event: string },
): Promise<IntegrationWebhookRecord> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_DOMAIN}/integrations/v2/${key}/webhooks`,
      body,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error creating webhook for integration ${key}:`, e);
    throw e;
  }
};

export const updateIntegrationWebhook = async (
  auth0: any,
  key: string,
  webhookId: string,
  body: { enabled: boolean },
): Promise<IntegrationWebhookRecord> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.patch(
      `${API_DOMAIN}/integrations/v2/${key}/webhooks/${webhookId}`,
      body,
      authConfig,
    );
    return response.data;
  } catch (e) {
    console.error(`Error updating webhook ${webhookId} for integration ${key}:`, e);
    throw e;
  }
};

export const getIntegrationWebhookLogs = async (
  auth0: any,
  key: string,
  webhookId: string,
  page: number,
  limit = 20,
): Promise<WebhookLogsPage> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    const response = await axios.get(
      `${API_DOMAIN}/integrations/v2/${key}/webhooks/${webhookId}/logs`,
      {
        ...authConfig,
        params: { page, limit },
      },
    );
    return response.data;
  } catch (e) {
    console.error(`Error fetching webhook logs for ${webhookId}:`, e);
    throw e;
  }
};

export const deleteIntegrationWebhook = async (
  auth0: any,
  key: string,
  webhookId: string,
): Promise<void> => {
  try {
    const authConfig = await getAuthHeaders(auth0);
    await axios.delete(`${API_DOMAIN}/integrations/v2/${key}/webhooks/${webhookId}`, authConfig);
  } catch (e) {
    console.error(`Error deleting webhook ${webhookId} for integration ${key}:`, e);
    throw e;
  }
};
